import React, { useState, useEffect } from 'react';
import { Box, Typography, List, ListItem, ListItemText, ListItemAvatar, Avatar, Chip, CircularProgress, Button, Grid } from '@mui/material';
import { Login, Logout, Schedule, Warning, Person } from '@mui/icons-material';
import ModernButton from '../common/ModernButton';
import axiosInstance from '../axiosConfig';
import moment from 'moment';

const AccessLogsWidget = ({ projectId, onSettings, onRemove }) => {
  const [accessLogs, setAccessLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (projectId) {
      fetchAccessLogs();
    }
  }, [projectId]);

  const consolidateLogsByEmployee = (logs) => {
    const consolidated = {};
    
    logs.forEach(log => {
      const employeeId = log.user_id || log.employee_id;
      const date = moment(log.entry_time || log.timestamp).format('YYYY-MM-DD');
      const key = `${employeeId}_${date}`;
      
      if (!consolidated[key]) {
        // Initialize with basic info
        consolidated[key] = {
          id: log.id,
          user_id: employeeId,
          name: log.name || log.employee_name,
          email: log.email,
          user_type: log.user_type,
          photo_url: log.photo_url,
          schedule: log.schedule,
          entry_time: log.entry_time || log.timestamp,
          exit_time: log.exit_time,
          workstation_entry_time: log.workstation_entry_time,
          workday_end: log.workday_end,
          break1_start: log.break1_start,
          break1_end: log.break1_end,
          break2_start: log.break2_start,
          break2_end: log.break2_end,
          break3_start: log.break3_start,
          break3_end: log.break3_end,
          // Keep track of the most recent timestamp for sorting
          latest_timestamp: log.entry_time || log.timestamp
        };
      } else {
        // Merge additional data from other records
        const existing = consolidated[key];
        
        // Update timestamps if we have more complete data
        if (log.workstation_entry_time && !existing.workstation_entry_time) {
          existing.workstation_entry_time = log.workstation_entry_time;
        }
        if (log.workday_end && !existing.workday_end) {
          existing.workday_end = log.workday_end;
        }
        if (log.exit_time && !existing.exit_time) {
          existing.exit_time = log.exit_time;
        }
        
        // Merge break data
        if (log.break1_start && !existing.break1_start) {
          existing.break1_start = log.break1_start;
          existing.break1_end = log.break1_end;
        }
        if (log.break2_start && !existing.break2_start) {
          existing.break2_start = log.break2_start;
          existing.break2_end = log.break2_end;
        }
        if (log.break3_start && !existing.break3_start) {
          existing.break3_start = log.break3_start;
          existing.break3_end = log.break3_end;
        }
        
        // Update latest timestamp
        const currentTime = moment(log.entry_time || log.timestamp);
        const existingTime = moment(existing.latest_timestamp);
        if (currentTime.isAfter(existingTime)) {
          existing.latest_timestamp = log.entry_time || log.timestamp;
        }
      }
    });
    
    // Convert back to array and sort by latest timestamp
    return Object.values(consolidated).sort((a, b) => 
      moment(b.latest_timestamp).diff(moment(a.latest_timestamp))
    );
  };

  const fetchAccessLogs = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/api/access-logs?limit=50');
      
      // Consolidate duplicate entries by employee and date
      const consolidatedLogs = consolidateLogsByEmployee(response.data);
      
      setAccessLogs(consolidatedLogs);
    } catch (error) {
      console.error('Error fetching access logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const getAccessTypeIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'entry':
      case 'login':
        return <Login fontSize="small" />;
      case 'exit':
      case 'logout':
        return <Logout fontSize="small" />;
      default:
        return <Schedule fontSize="small" />;
    }
  };

  const getAccessTypeColor = (type) => {
    switch (type?.toLowerCase()) {
      case 'entry':
      case 'login':
        return 'success';
      case 'exit':
      case 'logout':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatTime = (timestamp) => {
    return moment(timestamp).format('HH:mm');
  };

  const formatDate = (timestamp) => {
    const now = moment();
    const logDate = moment(timestamp);
    
    if (now.isSame(logDate, 'day')) {
      return 'Today';
    } else if (now.subtract(1, 'day').isSame(logDate, 'day')) {
      return 'Yesterday';
    } else {
      return logDate.format('MMM DD');
    }
  };

  const calculateEffectiveHoursWorked = (log) => {
    if (!log.workstation_entry_time || !log.workday_end || log.user_type === 'visitor') return 'N/A';
    
    try {
      const entry = moment(log.workstation_entry_time);
      const exit = moment(log.workday_end);
      let totalDuration = moment.duration(exit.diff(entry));
      
      // Subtract break violations
      const violations = calculateBreakViolations(log);
      if (violations) {
        const extraMinutes = violations.split(', ').reduce((total, violation) => {
          const minutes = parseInt(violation.match(/\d+/)[0]);
          return total + minutes;
        }, 0);
        
        totalDuration.subtract(extraMinutes, 'minutes');
      }
      
      const hours = totalDuration.asHours();
      return hours.toFixed(1);
    } catch (error) {
      return 'N/A';
    }
  };

  const calculateBreakViolations = (log) => {
    if (log.user_type === 'visitor') return null;
    
    const violations = [];
    
    // Check Break 1 (15 min limit)
    if (log.break1_start && log.break1_end) {
      const break1Start = moment(log.break1_start);
      const break1End = moment(log.break1_end);
      const break1Duration = moment.duration(break1End.diff(break1Start));
      const break1Minutes = break1Duration.asMinutes();
      
      if (break1Minutes > 15) {
        const extraMinutes = break1Minutes - 15;
        violations.push(`B1: +${extraMinutes.toFixed(0)}min`);
      }
    }
    
    // Check Break 2 (Lunch - 60 min limit)
    if (log.break2_start && log.break2_end) {
      const break2Start = moment(log.break2_start);
      const break2End = moment(log.break2_end);
      const break2Duration = moment.duration(break2End.diff(break2Start));
      const break2Minutes = break2Duration.asMinutes();
      
      if (break2Minutes > 60) {
        const extraMinutes = break2Minutes - 60;
        violations.push(`B2: +${extraMinutes.toFixed(0)}min`);
      }
    }
    
    // Check Break 3 (15 min limit)
    if (log.break3_start && log.break3_end) {
      const break3Start = moment(log.break3_start);
      const break3End = moment(log.break3_end);
      const break3Duration = moment.duration(break3End.diff(break3Start));
      const break3Minutes = break3Duration.asMinutes();
      
      if (break3Minutes > 15) {
        const extraMinutes = break3Minutes - 15;
        violations.push(`B3: +${extraMinutes.toFixed(0)}min`);
      }
    }
    
    return violations.length > 0 ? violations.join(', ') : null;
  };

  const getBreakViolationColor = (violations) => {
    if (!violations) return 'default';
    
    const totalExtraMinutes = violations.split(', ').reduce((total, violation) => {
      const minutes = parseInt(violation.match(/\d+/)[0]);
      return total + minutes;
    }, 0);
    
    if (totalExtraMinutes > 30) return 'error';
    if (totalExtraMinutes > 15) return 'warning';
    return 'info';
  };

  const calculateDailyStats = () => {
    const today = moment().startOf('day');
    const todayLogs = accessLogs.filter(log => 
      moment(log.entry_time || log.timestamp).isSame(today, 'day')
    );
    
    const employees = todayLogs.filter(log => log.user_type === 'employee');
    const visitors = todayLogs.filter(log => log.user_type === 'visitor');
    const lateEntries = employees.filter(log => {
      if (!log.schedule) return false;
      const entryTime = moment(log.entry_time || log.timestamp);
      const scheduleTime = moment(log.schedule, 'HH:mm');
      return entryTime.isAfter(scheduleTime);
    });
    
    return {
      total: todayLogs.length,
      employees: employees.length,
      visitors: visitors.length,
      late: lateEntries.length,
      onTime: employees.length - lateEntries.length
    };
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100%',
        minHeight: '300px'
      }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 2,
        flexShrink: 0
      }}>
        <Typography variant="body2" color="text.secondary">
          Últimas 50 entradas
        </Typography>
        <ModernButton
          size="small"
          variant="outlined"
          onClick={() => window.location.href = '/facility/access-logs'}
        >
          Ver Todos
        </ModernButton>
      </Box>

      {/* Daily Recap */}
      <Box sx={{ 
        mb: 2, 
        p: 2, 
        backgroundColor: 'rgba(255, 255, 255, 0.8)', 
        borderRadius: 2, 
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 255, 255, 0.3)',
        flexShrink: 0
      }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          Resumen del Día - {moment().format('DD/MM/YYYY')}
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={3}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                {calculateDailyStats().total}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Total
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={3}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'success.main' }}>
                {calculateDailyStats().employees}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Empleados
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={3}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'info.main' }}>
                {calculateDailyStats().visitors}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Visitantes
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={3}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'error.main' }}>
                {calculateDailyStats().late}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Retrasos
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ 
        flex: 1, 
        overflow: 'auto',
        '&::-webkit-scrollbar': {
          width: '6px',
          height: '6px',
        },
        '&::-webkit-scrollbar-track': {
          background: 'rgba(0, 0, 0, 0.1)',
          borderRadius: '3px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: 'rgba(0, 0, 0, 0.3)',
          borderRadius: '3px',
          '&:hover': {
            background: 'rgba(0, 0, 0, 0.5)',
          },
        },
      }}>
        {accessLogs.length === 0 ? (
          <Box sx={{ 
            textAlign: 'center', 
            py: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%'
          }}>
            <Schedule sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="body2" color="text.secondary">
              No se encontraron registros de acceso
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 0 }}>
            {accessLogs.slice(0, 10).map((log) => (
              <ListItem 
                key={log.id} 
                sx={{ 
                  px: 0, 
                  py: 1,
                  borderRadius: 1,
                  mb: 1,
                  backgroundColor: 'rgba(255, 255, 255, 0.5)',
                  backdropFilter: 'blur(10px)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                    transform: 'translateX(4px)',
                  }
                }}
              >
                <ListItemAvatar>
                  <Avatar 
                    sx={{ 
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      width: 36,
                      height: 36
                    }}
                    src={log.photo_url || log.profile_image || log.avatar_url}
                    alt={log.name || 'User'}
                  >
                    {log.name?.charAt(0) || <Person />}
                  </Avatar>
                </ListItemAvatar>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1, 
                  mr: 1,
                  color: getAccessTypeColor(log.access_type) === 'success' ? 'success.main' : 'error.main'
                }}>
                  {getAccessTypeIcon(log.access_type)}
                </Box>
                <ListItemText
                  primary={log.name || log.employee_name || log.visitor_name || 'Desconocido'}
                  secondary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="caption" color="text.secondary">
                        {formatTime(log.entry_time || log.timestamp)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(log.entry_time || log.timestamp)}
                      </Typography>
                      {log.user_type === 'employee' && (
                        <Typography variant="caption" color="primary" sx={{ fontWeight: 600 }}>
                          {calculateEffectiveHoursWorked(log)}h
                        </Typography>
                      )}
                    </Box>
                  }
                  primaryTypographyProps={{ 
                    variant: 'body2', 
                    sx: { fontWeight: 600 } 
                  }}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                  <Chip
                    label={log.access_type || log.user_type === 'visitor' ? 'Visitante' : 'Empleado'}
                    color={getAccessTypeColor(log.access_type)}
                    size="small"
                    sx={{ 
                      borderRadius: 2,
                      fontWeight: 500
                    }}
                  />
                  {(() => {
                    const violations = calculateBreakViolations(log);
                    if (!violations) return null;
                    
                    return (
                      <Chip
                        icon={<Warning />}
                        label={violations}
                        color={getBreakViolationColor(violations)}
                        size="small"
                        sx={{ 
                          borderRadius: 2,
                          fontWeight: 500
                        }}
                      />
                    );
                  })()}
                </Box>
              </ListItem>
            ))}
            {accessLogs.length > 10 && (
              <ListItem sx={{ 
                px: 0, 
                py: 1,
                borderRadius: 1,
                backgroundColor: 'rgba(255, 255, 255, 0.3)',
                backdropFilter: 'blur(10px)'
              }}>
                <ListItemText
                  primary={`+${accessLogs.length - 10} registros más`}
                  primaryTypographyProps={{ 
                    variant: 'caption', 
                    color: 'text.secondary',
                    sx: { fontWeight: 500 }
                  }}
                />
                <ModernButton
                  size="small"
                  variant="text"
                  onClick={() => window.location.href = '/facility/access-logs'}
                >
                  Ver Todos
                </ModernButton>
              </ListItem>
            )}
          </List>
        )}
      </Box>
    </Box>
  );
};

export default AccessLogsWidget; 